import { environment } from '../../../environments/environment';

/**
 * Whether a request URL belongs to us, and may therefore carry the user's
 * access token.
 *
 * `AuthInterceptor` attaches `Authorization: Bearer <token>` to everything it
 * handles, and HttpClient is not only used against our own backend - the
 * sales-forecast isochrone call goes to api.mapbox.com. Without this check
 * every logged-in visitor hands their session token to a third party that has
 * no use for it (Mapbox authenticates by `access_token` query param) and no
 * reason to be trusted with it.
 *
 * Origin-based rather than a prefix match on `environment.apiUrl`, because
 * relative URLs and same-origin assets must keep working untouched, and
 * because local development serves the app from :4200 while the API lives on
 * another origin entirely.
 */
export function isOwnApiUrl(url: string): boolean {
  const base = typeof window !== 'undefined' ? window.location.origin : '';
  try {
    const origin = new URL(url, base || undefined).origin;
    return origin === base || apiOrigins().includes(origin);
  } catch {
    // Unparseable: keep the pre-existing behaviour rather than silently
    // dropping auth from a request we do not understand.
    return true;
  }
}

/** Origins of our own services, per the active environment. */
function apiOrigins(): string[] {
  return [environment.apiUrl, (environment as { aiChatUrl?: string }).aiChatUrl]
    .filter((url): url is string => !!url)
    .map((url) => {
      try {
        return new URL(url).origin;
      } catch {
        return null;
      }
    })
    .filter((origin): origin is string => origin !== null);
}
